/** Matches MRP Plot Register spreadsheet layout. */

export type PlotImportFieldKey =
  | 'plotNumber'
  | 'mainAvenue'
  | 'courtOrStreet'
  | 'section'
  | 'notes'
  | 'boreholeMeterNumber'
  | 'kplcMeterNumber'

export interface PlotImportSheetColumn {
  header: string
  importKey?: PlotImportFieldKey
  imported: boolean
}

export const PLOT_IMPORT_SHEET_COLUMNS: PlotImportSheetColumn[] = [
  { header: 'Plot No.', importKey: 'plotNumber', imported: true },
  { header: 'Main Avenue', importKey: 'mainAvenue', imported: true },
  { header: 'Court / Street', importKey: 'courtOrStreet', imported: true },
  { header: 'Section', importKey: 'section', imported: true },
  { header: 'Notes', importKey: 'notes', imported: true },
  { header: 'Borehole meter no.', importKey: 'boreholeMeterNumber', imported: true },
  { header: 'KPLC meter no.', importKey: 'kplcMeterNumber', imported: true },
]

export const PLOT_IMPORT_TEMPLATE_EXAMPLE_ROW: string[] = [
  '322',
  'Riverpark Avenue',
  'Athi Crescent',
  'West',
  '',
  '00895',
  '12345678',
]

export function normalizeImportHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\./g, '').replace(/\//g, '_').replace(/\s+/g, '_')
}

const HEADER_ALIASES: Record<PlotImportFieldKey, string[]> = {
  plotNumber: ['plot_no', 'plot_number', 'plot'],
  mainAvenue: ['main_avenue', 'mainavenue'],
  courtOrStreet: ['court_street', 'court___street', 'court_or_street', 'court'],
  section: ['section'],
  notes: ['notes', 'note'],
  boreholeMeterNumber: ['borehole_meter_no', 'borehole_meter_number', 'borehole_meter', 'borehole'],
  kplcMeterNumber: ['kplc_meter_no', 'kplc_meter_number', 'kplc_meter', 'kplc'],
}

export function mapPlotImportHeaderFields(
  headers: string[],
): Partial<Record<PlotImportFieldKey, number>> {
  const map: Partial<Record<PlotImportFieldKey, number>> = {}

  headers.forEach((header, index) => {
    const key = normalizeImportHeader(header)
    for (const [field, aliases] of Object.entries(HEADER_ALIASES) as [
      PlotImportFieldKey,
      string[],
    ][]) {
      if (aliases.includes(key)) {
        map[field] = index
      }
    }
  })

  return map
}

/** Slug for synthetic special-use plot numbers. */
export function slugifyPlotLabel(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
}

const SPECIAL_PLOT_MARKERS = ['—', '-', '–', 'SPECIAL-USE']

export function isSpecialUsePlotNumber(raw: string): boolean {
  const t = raw.trim()
  if (!t) return true
  if (SPECIAL_PLOT_MARKERS.some((m) => t === m || t.startsWith('SPECIAL-USE'))) return true
  return false
}

/** Resolve plot number for DB: numeric string or SPECIAL-{slug}. */
export function resolvePlotNumber(
  rawPlotNumber: string,
  courtOrStreet: string,
  notes: string,
): { newNumber: string; plotType: 'residential' | 'special_use' } | null {
  const plotNo = rawPlotNumber.trim()
  const court = courtOrStreet.trim()

  if (isSpecialUsePlotNumber(plotNo)) {
    const label = court || notes.replace(/special use.*/i, '').trim()
    if (!label) return null
    const slug = slugifyPlotLabel(label)
    if (!slug) return null
    return { newNumber: `SPECIAL-${slug}`, plotType: 'special_use' }
  }

  const digits = plotNo.replace(/\D/g, '')
  if (!digits) return null

  return { newNumber: String(Number(digits)), plotType: 'residential' }
}
